import {ContactDocument} from 'common'
import Pagination from 'components/Pagination'
import ReqNotFoundScreen from 'components/ReqNotFoundScreen'
import {TABLE_PAGINATION_PAGE_SIZE} from 'components/constants'
import {Timestamp} from 'firebase/firestore'
import debounce from 'lodash/debounce'
import isDate from 'lodash/isDate'
import React, {useEffect, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {CheckedIcon, UncheckedIconTwo} from 'assets/images'
import AlgoliaClient, {
  CONTACTS_ADMIN_PANEL_ALGOLIA_INDEX
} from '../AlgoliaSearch'
import Button from '../Button'
import {useAppContext, useFirebase} from '../Firebase/hooks'
import InputField from '../InputField'
import Layout from '../Layout'
import {Loader} from '../Loader'
import NavBar from '../Navbar'
import withAuthorization from '../Session/withAuthorization'
import Table from '../TableComponent'
import {
  convertToDate,
  getFormattedDate,
  getUserFullName,
  searchWithAlgolia
} from '../utils'
import styles from './ContactPage.module.scss'

const ContactPage = ({customClass}: {customClass: string}) => {
  const {state} = useLocation()
  const pageFromLocation = state?.page
  const [currentPage, setCurrentPage] = useState<number>(pageFromLocation || 1)
  const [contactData, setContactsData] = useState<Array<ContactDocument>>([])
  const [accountList, setAccountList] = useState<Array<string>>([])
  const [loader, setLoader] = useState(true)
  const {getUserAccounts} = useFirebase()
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState('')
  const searchIndex = AlgoliaClient.initIndex(
    CONTACTS_ADMIN_PANEL_ALGOLIA_INDEX
  )
  const [dir, setDir] = useState<{key: string; dir: string}>({
    key: '',
    dir: 'asc'
  })
  const [pageCount, setPageCount] = useState<number>(0)
  const {
    appState: {isMSAAdmin, isSuperAdmin, isSuperManager}
  } = useAppContext()

  const getMSAAccountsList = async () => {
    const userAccounts = await getUserAccounts()
    const accountIDLength = userAccounts.length
    if (accountIDLength) {
      !accountList.length && setAccountList([...userAccounts])
    }
  }

  useEffect(() => {
    if (isMSAAdmin) {
      getMSAAccountsList()
    }
  }, [])

  useEffect(() => {
    searchIndex
      .setSettings({
        customRanking: ['desc(createdAt)']
      })
      .then(() => {
        AlgoliaClient.clearCache()
        if (isMSAAdmin && accountList.length) {
          searchWithAlgolia<ContactDocument>({
            searchQuery: '*',
            searchIndex,
            setLoaderState: setLoader,
            setData: setContactsData,
            setPageCount,
            accountList,
            currentPage,
            sortData: false
          }).then(() => setLoader(false))
          return
        }
        if (isSuperAdmin || isSuperManager) {
          searchWithAlgolia<ContactDocument>({
            searchQuery: '*',
            searchIndex,
            setLoaderState: setLoader,
            setData: setContactsData,
            setPageCount,
            accountList,
            currentPage,
            sortData: false
          }).then(() => setLoader(false))
        }
      })
  }, [accountList?.length])

  const tableTitles = [
    {title: 'Contact Name', sortBy: () => sortBy(['firstName'])},
    {title: 'Account Name', sortBy: () => sortBy(['accountName'])},
    {title: 'Is Primary Contact'},
    {title: 'Status'},
    {title: 'Mobile'},
    {title: 'Created At', sortBy: () => sortBy(['createdAt'])}
  ]

  const getFormattedData = (
    contactData: ContactDocument & {objectID?: string}
  ) => {
    const {contactInfo, firstName, lastName, objectID} = contactData || {}
    const mobileNumber = contactInfo?.mobileNumber || '-'
    const userfirstName = firstName || ''
    const userlastName = lastName || ''
    const contactName = getUserFullName(userfirstName, userlastName)
    const companyName = (typeof contactData?.accountName == "string") ?  contactData?.accountName : contactData?.accountName?.join(",<br/>") || ''
    const isPrimaryContact = contactData?.isPrimaryContact || false
    const isActive = contactData?.isActive ?? true
    const createdAtDate =
      contactData?.createdAt && convertToDate(contactData?.createdAt as any)
    const createdAt = isDate(createdAtDate)
      ? getFormattedDate(createdAtDate)
      : '-'
    const date = isDate(createdAtDate)
      ? Timestamp.fromDate(createdAtDate).seconds
      : '-'
    const documentId = contactData?.contactId || ''
    return {
      contactName,
      isPrimaryContact,
      isActive,
      mobileNumber,
      createdAt,
      documentId,
      companyName,
      objectID,
      date
    }
  }

  const getRowData = (
    rowData: Array<{
      contactName: string
      isPrimaryContact: boolean
      isActive: boolean
      mobileNumber: string
      createdAt: string
      documentId: string
      companyName: string
      objectID?: string
    }>
  ) => {
    return (rowData || []).map((singleRowData, index) => {
      const {
        contactName,
        isPrimaryContact,
        isActive,
        mobileNumber,
        createdAt,
        documentId,
        companyName,
        objectID
      } = singleRowData || {}
      return (
        <tr
          key={`contact-row-data-${index}`}
          onClick={() => {
            navigate('/contacts/single-contact-details', {
              state: {id: documentId, objectID, isEdit: true, currentPage},
              preventScrollReset: true
            })
          }}
        >
          <td>{contactName || '-'}</td>
          <td dangerouslySetInnerHTML={{__html: companyName}}></td>
          <td>
            <img
              src={isPrimaryContact ? CheckedIcon : UncheckedIconTwo}
              alt="primary"
            />
          </td>
          <td>
            <span className={isActive ? styles.activeStatus : styles.inactiveStatus}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td>{mobileNumber}</td>
          <td>{createdAt}</td>
        </tr>
      )
    })
  }

  const debouncedSearch: any = useRef(debounce(searchWithAlgolia, 800))

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isMSAAdmin && !accountList?.length) return
    const value = event?.target?.value
    setSearchValue(value)
    if (value) {
      debouncedSearch?.current({
        searchQuery: value,
        searchIndex: searchIndex,
        setData: setContactsData,
        setLoaderState: setLoader,
        setPageCount,
        accountList: accountList,
        sortData: false
      })
    } else {
      debouncedSearch?.current({
        searchQuery: '*',
        searchIndex,
        setLoaderState: setLoader,
        setData: setContactsData,
        setPageCount,
        accountList,
        currentPage,
        sortData: false
      })
    }
  }

  const noData = !contactData.length && !loader

  const scrollRef: React.RefObject<HTMLDivElement> = React.createRef()

  const scrollToRef = () =>
    scrollRef?.current?.scrollIntoView({behavior: 'smooth'})

  const items = contactData.map((data) => getFormattedData(data))

  const showPagination = pageCount > TABLE_PAGINATION_PAGE_SIZE

  const onPageChange = (page: number) => {
    setLoader(true)
    setCurrentPage(page)
    debouncedSearch?.current({
      searchQuery: searchValue,
      searchIndex,
      setLoaderState: setLoader,
      setData: setContactsData,
      setPageCount,
      accountList,
      currentPage: page,
      sortData: false
    })
    setLoader(false)
    scrollToRef()
  }

  const sortBy = async (keys: Array<string>) => {
    setLoader(true)
    let newDir = 'asc'
    if (dir.key === keys[0]) {
      newDir = dir.dir === 'asc' ? 'desc' : 'asc'
    }
    setDir({dir: newDir, key: keys[0]})
    await searchIndex.setSettings({
      customRanking: keys.map((key) => newDir + '(' + key + ')')
    })
    setCurrentPage(1)
    await AlgoliaClient.clearCache()
    setTimeout(() => {
      debouncedSearch?.current({
        searchQuery: searchValue,
        searchIndex,
        setLoaderState: setLoader,
        setData: setContactsData,
        setPageCount,
        currentPage: 1,
        sortData: false
      })
    }, 1000)
  }
  return (
    <div className={styles.withLayout}>
      <Layout />
      <div className={[styles.container, customClass].join(' ')}>
        <NavBar title="Contacts" />
        <div className={styles.button_filters}>
          <InputField
            name="contactSearchField"
            type="text"
            value={searchValue}
            onChange={handleSearch}
            isSearch={true}
            customClass={styles.inputField}
            placeholder="Search for Filters"
          />
          <div className={styles.button_creaters}>
            <Button
              onClick={() => navigate('/contacts/single-contact-details')}
              text={'Create Contact'}
              customClass={styles.creater_button}
            />
          </div>
        </div>
        {loader ? (
          <Loader />
        ) : noData ? (
          <ReqNotFoundScreen />
        ) : (
          <React.Fragment>
            <Table titles={tableTitles} rowData={getRowData(items)} />
            {showPagination && (
              <div className="pagination__wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalCount={pageCount}
                  pageSize={TABLE_PAGINATION_PAGE_SIZE}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default withAuthorization(ContactPage)
